<template>
  <a-select
    v-model:value="innerValue"
    :options="selectOptions"
    :placeholder="placeholder"
    :disabled="disabled || !selectOptions.length"
    :size="size"
    :allow-clear="allowClear"
    :show-search="showSearch"
    :filter-option="filterAreaOption"
    :loading="loading"
    :status="status"
    :not-found-content="notFoundContent"
    class="ant-ward-select"
    @change="onChange"
  />
</template>

<script lang="ts" setup>
import { computed, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";
import {
  filterAreaOption,
  mapAreaOptions,
  findAreaById,
  type AreaNode,
  type AreaSelectOption,
} from "./useArea";

interface Props {
  modelValue?: string | number | null;
  /** Danh sách phường/xã (thường lấy từ children của quận/huyện) */
  options?: AreaNode[];
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  showSearch?: boolean;
  loading?: boolean;
  status?: "" | "error" | "warning";
  notFoundContent?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  options: () => [],
  placeholder: "Chọn Phường / Xã",
  disabled: false,
  size: "middle",
  allowClear: true,
  showSearch: true,
  loading: false,
  status: "",
  notFoundContent: "Không có dữ liệu",
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
  (e: "change", value: string | number | null, option: AreaSelectOption | null): void;
}>();

const selectOptions = computed(() => mapAreaOptions(props.options));

const innerValue = computed({
  get: () => props.modelValue,
  set: (val: string | number | null) => emit("update:modelValue", val),
});

watch(
  () => props.options,
  () => {
    if (
      props.modelValue != null &&
      !findAreaById(props.options, props.modelValue)
    ) {
      emit("update:modelValue", null);
      emit("change", null, null);
    }
  }
);

const onChange = (val: string | number | null, option: any) => {
  const selected = (option as AreaSelectOption) || null;
  emit("update:modelValue", val);
  emit("change", val, selected);
};
</script>

<style scoped>
.ant-ward-select {
  width: 100%;
}
</style>

<!--
  Hướng dẫn sử dụng — Ward Select
  Global: <h-ward v-model="ward" :options="wards" />
  Import: import { WardSelect } from '@/components/ant/address'

  @change(value, option)
  :options lấy từ children của huyện
  Docs: src/components/ant/address/README.md
-->
